import { Chip, Stack, Typography } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { DynamicIcon } from 'src/components/DynamicIcon/dynamic-icon';
import { GroupedSelectMenu } from 'src/components/SelectMenu';
import type { ConversationAssigneeGroupOption } from 'src/graphql/entities/conversation-assignee-group';
import {
  AUDIENCE_KIND_ICONS,
  buildAudienceOptions,
  encodeAudienceValue,
  fromAudienceValues,
  MacroAudienceKind,
  type MacroAudienceSelection,
  toAudienceValues,
} from 'src/graphql/entities/macro/macro-audience';
import type { UserGroupOption } from 'src/graphql/entities/user-group';

interface MacroAudienceFieldProps {
  selected: MacroAudienceSelection;
  available: MacroAudienceSelection;
  loading?: boolean;
  onChange: (selection: MacroAudienceSelection) => void;
}

/**
 * The audience picker for a Shared macro: one menu over both group kinds, with
 * the selection shown as removable chips beneath it.
 *
 * Empty is a real state, not an unfinished one — it means the macro is
 * available to everyone — so the trigger reads "Everyone" rather than nudging
 * for a selection.
 */
export const MacroAudienceField = ({
  selected,
  available,
  loading,
  onChange,
}: MacroAudienceFieldProps) => {
  const [search, setSearch] = useState('');

  const options = useMemo(() => buildAudienceOptions(available), [available]);
  const values = useMemo(() => toAudienceValues(selected), [selected]);

  const onValuesChange = useCallback(
    (nextValues: string[]) => {
      onChange(fromAudienceValues(nextValues, available, selected));
    },
    [available, onChange, selected],
  );

  const removeGroup = useCallback(
    (kind: MacroAudienceKind, groupId: string) => {
      const removed = encodeAudienceValue(kind, groupId);
      onValuesChange(values.filter((value) => value !== removed));
    },
    [onValuesChange, values],
  );

  const selectedCount = values.length;

  return (
    <Stack gap={1}>
      <Typography variant="body2">Available to</Typography>
      <GroupedSelectMenu
        label="Available to"
        multiple
        options={options}
        values={values}
        onValuesChange={onValuesChange}
        placeholder="Everyone"
        triggerContent={selectedCount ? `${selectedCount} selected` : undefined}
        search
        searchPlaceholder="Search groups…"
        searchValue={search}
        onSearchChange={setSearch}
        loading={loading}
      />
      {selectedCount ? (
        <Stack direction="row" gap={0.5} flexWrap="wrap">
          {selected.userGroups.map((group: UserGroupOption) => (
            <Chip
              key={`user-group-${group.value}`}
              size="small"
              label={group.label}
              icon={
                <DynamicIcon
                  library="lucide"
                  name={AUDIENCE_KIND_ICONS[MacroAudienceKind.UserGroup]}
                />
              }
              onDelete={() => removeGroup(MacroAudienceKind.UserGroup, String(group.value))}
            />
          ))}
          {selected.conversationAssigneeGroups.map(
            (group: ConversationAssigneeGroupOption) => (
              <Chip
                key={`assignee-group-${group.value}`}
                size="small"
                label={group.label}
                icon={
                  <DynamicIcon
                    library="lucide"
                    name={AUDIENCE_KIND_ICONS[MacroAudienceKind.AssigneeGroup]}
                  />
                }
                onDelete={() =>
                  removeGroup(MacroAudienceKind.AssigneeGroup, String(group.value))
                }
              />
            ),
          )}
        </Stack>
      ) : null}
    </Stack>
  );
};
